import { format, parseISO } from 'date-fns'

import { formatDate, isDefAndNotEmpty } from 'utils'
import { EventType, IEvent, Nullable } from 'types'
import { ReactNode } from 'react'

const isCirclesIntersect = (
  x0: number,
  y0: number,
  r0: number,
  x1: number,
  y1: number,
  r1: number,
) => {
  return Math.hypot(x0 - x1, y0 - y1) <= r0 + r1
}

interface Props {
  days: Date[]
  events: Nullable<IEvent[]>
  dateFormat: string
  timeFormat: string
  eventsMap: Record<EventType, { title: string; color: string }>
  timeToPixels: (date: Date) => number
  margin: number
  dayWidth: number
  gapWidth: number
  onShowTooltip: (e: React.MouseEvent, text: ReactNode) => void
  onHideTooltip: () => void
}

export const OneTimeEvents = (props: Props) => {
  const {
    days,
    events,
    dateFormat,
    timeFormat,
    eventsMap,
    timeToPixels,
    margin,
    dayWidth,
    gapWidth,
    onShowTooltip,
    onHideTooltip,
  } = props

  return events?.map((event, index) => {
    const start = parseISO(event.startTime)
    const dayIndex = days.findIndex((d) => format(d, dateFormat) === format(start, dateFormat))

    if (dayIndex === -1) return null

    let x = margin + dayIndex * dayWidth + dayWidth / 2 + dayIndex * gapWidth
    const y = timeToPixels(start)
    const r = 8

    const restEvents = events?.slice(index + 1)

    const intersections = restEvents.filter((it) => {
      const restEventStart = parseISO(it.startTime)
      const y2 = timeToPixels(restEventStart)

      const restEventDayIndex = days.findIndex(
        (d) => format(d, dateFormat) === format(restEventStart, dateFormat),
      )

      // Если события в пределах одного дня и их координаты пересекаются
      if (it.id !== event.id && restEventDayIndex === dayIndex) {
        return isCirclesIntersect(x, y, r, x, y2, r)
      }

      return false
    })

    // Если есть перекрывающиеся круги, сдвигаем текущий круг вправо от предыдущих
    if (isDefAndNotEmpty(intersections)) {
      x += intersections.length * 5
    }

    const eventFromMap = eventsMap[event.eventTypeCode as EventType]
    const tooltipJsx = (
      <>
        <div className="event-name">{eventFromMap?.title}</div>
        {formatDate(event.startTime, timeFormat)}
      </>
    )

    return (
      <circle
        key={`single-${event.id}`}
        cx={x}
        cy={y}
        r={r}
        fill={eventFromMap?.color}
        onMouseEnter={(e) => onShowTooltip(e, tooltipJsx)}
        onMouseLeave={onHideTooltip}
      />
    )
  })
}
